import { useRef } from 'react'
import { NextLink } from '@/features/shared/elements/Link'
import { getProductCardAriaLabel } from '@/features/shop/components/ProductCard/Utils'
import { useIsVisitorOrZipOnly } from '@/features/account/services/User/hooks'
import {
  type OnProductClickCallback,
  type ProductCardProduct,
} from '@/features/shop/components/ProductCard/types'
import { useGuestParams } from '@/features/account/services/GuestUser/hooks'
import { useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { useQueryClient } from '@tanstack/react-query'
import { pdpProductQueryOptions } from '@/features/shop/services/Product/queries'
import { getProductPageUrl } from '@/features/shared/utils/getProductPageUrl'

export type ProductLinkProps = {
  'aria-label'?: string
  className?: string
  index: number
  product: ProductCardProduct
  onClick: OnProductClickCallback
  children?: React.ReactNode
  linkToUniversalPDP: boolean
  /** Overrides the product's PDP link so the card navigates elsewhere (e.g. a list detail page). */
  href?: string
}

export const ProductLink = ({
  index,
  product,
  onClick,
  children,
  className,
  'aria-label': ariaLabel,
  linkToUniversalPDP,
  href,
}: ProductLinkProps) => {
  const queryClient = useQueryClient()
  const storeParams = useStoreParams()
  const { guestAddress } = useGuestParams() ?? {}
  const isLinkToUniversalPDP =
    (useIsVisitorOrZipOnly() && !guestAddress?.zip_code) || linkToUniversalPDP
  const linkAriaLabel = ariaLabel ?? getProductCardAriaLabel(product)

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handlePrefetchPdp = () => {
    if (isLinkToUniversalPDP) return

    queryClient.query(pdpProductQueryOptions({ storeParams, productId: product.id }))
    const pdpProductUrl = product.image.original_size_url ?? product.image.url
    const img = new Image()
    img.src = pdpProductUrl
  }

  const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onClick({ index, product }, e)
    handlePrefetchPdp()
  }

  const handleSchedulePrefetchPdp = () => {
    handleClearTimeout()
    timeoutRef.current = setTimeout(handlePrefetchPdp, 1000)
  }

  const handleClearTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }

  if (href) {
    return (
      <NextLink
        prefetch={false}
        className={className}
        href={href}
        onClick={(e) => onClick({ index, product }, e)}
        aria-label={linkAriaLabel}
      >
        {children}
      </NextLink>
    )
  }

  if (isLinkToUniversalPDP) {
    return (
      <NextLink
        // disable prefetch so we don't fetch PDP JSON for every product link in view.
        // nextjs will automatically prefetch the linked page on hover instead.
        prefetch={false}
        className={className}
        onClick={handleOnClick}
        aria-label={linkAriaLabel}
        href={getProductPageUrl({
          id: product.id,
          universal_id: product.universal_id,
          ungated: true,
        })}
      >
        {children}
      </NextLink>
    )
  }
  return (
    <NextLink
      // disable prefetch so we don't fetch PDP JSON for every product link in view.
      // nextjs will automatically prefetch the linked page on hover instead.
      prefetch={false}
      className={className}
      href={getProductPageUrl({
        id: product.id,
        universal_id: product.universal_id,
      })}
      onClick={handleOnClick}
      aria-label={linkAriaLabel}
      onFocus={handleSchedulePrefetchPdp}
      onBlur={handleClearTimeout}
      onPointerEnter={handleSchedulePrefetchPdp}
      onPointerLeave={handleClearTimeout}
    >
      {children}
    </NextLink>
  )
}
